import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowLeft, Lock, CheckCircle2, AlertCircle } from "lucide-react";
import { t } from "../lib/i18n";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Algo ha fallado.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" · ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) setError("Enlace inválido. Solicita uno nuevo desde 'Olvidé mi contraseña'.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!pwd || pwd.length < 8) {
      setError("La contraseña debe tener al menos 8 caracteres.");
      return;
    }
    if (pwd !== pwd2) {
      setError("Las contraseñas no coinciden.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      setSuccess(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-16 bg-background">
      <div className="max-w-md w-full space-y-7 fade-up">
        <a href="/" className="flex items-center gap-3" data-testid="reset-logo">
          <div className="w-7 h-7 hairline flex items-center justify-center bg-[#1E3A5F]">
            <span className="text-bone font-serif-display text-sm leading-none">R</span>
          </div>
          <span className="font-serif-display text-xl tracking-tight">{t("Relevo")}</span>
        </a>

        {success ? (
          <div className="space-y-6">
            <div className="w-12 h-12 hairline flex items-center justify-center bg-forest/10">
              <CheckCircle2 className="w-6 h-6 text-forest" />
            </div>
            <div className="space-y-3">
              <span className="font-mono-label text-gold">{t("Contraseña actualizada")}</span>
              <h1 className="font-serif-display text-3xl md:text-4xl leading-tight">{t("Listo. Te redirigimos…")}</h1>
              <p className="text-muted-foreground">
                {t("Tu nueva contraseña ya está activa. Por seguridad hemos cerrado todas tus sesiones activas.")}
              </p>
            </div>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 font-mono-label text-gold hover:underline"
              data-testid="reset-go-login"
            >
              {t("Ir al inicio de sesión")}{" "}<ArrowLeft className="w-3 h-3 rotate-180" />
            </Link>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              <span className="font-mono-label text-gold">{t("Nueva contraseña")}</span>
              <h1 className="font-serif-display text-4xl md:text-5xl leading-tight">{t("Crea tu nueva contraseña.")}</h1>
              <p className="text-muted-foreground text-sm">
                {t("Mínimo 8 caracteres, debe incluir letras y al menos un número.")}
              </p>
            </div>

            <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
              <Field icon={<Lock className="w-4 h-4" />} label={t("Nueva contraseña")}>
                <Input
                  type="password"
                  value={pwd}
                  onChange={(e) => setPwd(e.target.value)}
                  placeholder="••••••••"
                  className="rounded-none pl-10"
                  data-testid="reset-password-input"
                  autoComplete="new-password"
                  autoFocus
                />
              </Field>
              <Field icon={<Lock className="w-4 h-4" />} label={t("Repite la contraseña")}>
                <Input
                  type="password"
                  value={pwd2}
                  onChange={(e) => setPwd2(e.target.value)}
                  placeholder="••••••••"
                  className="rounded-none pl-10"
                  data-testid="reset-password-confirm"
                  autoComplete="new-password"
                />
              </Field>

              {error && (
                <div className="hairline bg-destructive/5 text-destructive px-3 py-2 text-sm flex items-start gap-2" data-testid="reset-error">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <Button
                type="submit"
                disabled={loading || !token}
                className="rounded-none w-full bg-[#1E3A5F] hover:bg-[#2B4C78] text-bone h-12 disabled:opacity-50"
                data-testid="reset-submit-btn"
              >
                {loading ? t("Actualizando…") : t("Cambiar contraseña")}
              </Button>
            </form>

            <Link
              to="/login"
              className="inline-flex items-center gap-2 font-mono-label text-muted-foreground hover:text-foreground"
              data-testid="reset-cancel"
            >
              <ArrowLeft className="w-3 h-3" />{" "}{t("Volver al inicio de sesión")}
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

const Field = ({ icon, label, children }) => (
  <div className="space-y-1.5">
    <Label className="font-mono-label text-muted-foreground">{label}</Label>
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">{icon}</span>
      {children}
    </div>
  </div>
);
